import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

export default function CategoryDetail() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();

  const categoryData = {
    ai: {
      name: "AI Writing & Research Tools",
      icon: "🤖",
      desc: "Learn to use Claude, ChatGPT, Perplexity, and other AI assistants to accelerate academic research and writing.",
      tools: [
        { name: "Claude", desc: "Advanced AI assistant for research and writing", color: "#0f766e" },
        { name: "ChatGPT", desc: "Popular AI model for various academic tasks", color: "#10a37f" },
        { name: "Perplexity", desc: "Citation-backed AI search for research", color: "#5b21b6" },
      ],
    },
    cite: {
      name: "Citation & Reference Managers",
      icon: "📚",
      desc: "Master citation tools and build powerful reference libraries.",
      tools: [
        { name: "Zotero", desc: "Free and open-source reference management", color: "#cc0000" },
        { name: "Mendeley", desc: "Collaborative reference management tool", color: "#d32f2f" },
        { name: "EndNote", desc: "Professional citation management software", color: "#1e40af" },
      ],
    },
    data: {
      name: "Data Analysis & Statistics",
      icon: "📊",
      desc: "Learn data analysis, visualization, and statistical methods.",
      tools: [
        { name: "SPSS", desc: "Statistical analysis and data management", color: "#00a0d0" },
        { name: "Python", desc: "Matplotlib, Seaborn, Pandas for data work", color: "#3776ab" },
        { name: "R", desc: "Statistical computing and graphics", color: "#276dc3" },
      ],
    },
    pm: {
      name: "Project & Task Management",
      icon: "🗂️",
      desc: "Plan, organize, and track your academic projects.",
      tools: [
        { name: "Notion", desc: "All-in-one workspace for projects", color: "#000000" },
        { name: "Trello", desc: "Visual project management with boards", color: "#0079bf" },
        { name: "Asana", desc: "Team collaboration and project tracking", color: "#f06a4d" },
      ],
    },
  };

  const allTutorials = {
    ai: [
      { title: "Getting Started with Claude for Academic Research", level: "Beginner", time: "15 min" },
      { title: "Using Perplexity AI for Source Discovery", level: "Beginner", time: "12 min" },
      { title: "Advanced ChatGPT Prompting for Literature Reviews", level: "Advanced", time: "25 min" },
      { title: "AI Tools for Thesis Writing", level: "Intermediate", time: "30 min" },
    ],
    cite: [
      { title: "Setting Up Zotero with APA 7th Edition", level: "Beginner", time: "20 min" },
      { title: "Mendeley for Collaborative Research", level: "Intermediate", time: "18 min" },
      { title: "EndNote Advanced Features", level: "Advanced", time: "35 min" },
      { title: "Citation Styles: APA, Harvard, Vancouver", level: "Beginner", time: "22 min" },
    ],
    data: [
      { title: "Running Descriptive Statistics in SPSS", level: "Intermediate", time: "30 min" },
      { title: "Data Visualisation with Python", level: "Advanced", time: "45 min" },
      { title: "Statistical Hypothesis Testing", level: "Advanced", time: "40 min" },
      { title: "R for Data Science: Getting Started", level: "Beginner", time: "28 min" },
    ],
    pm: [
      { title: "Planning Your Dissertation with Notion", level: "All Levels", time: "25 min" },
      { title: "Agile Methodology for Research Projects", level: "Intermediate", time: "20 min" },
      { title: "Gantt Charts with Asana", level: "Beginner", time: "15 min" },
      { title: "Team Collaboration in Academic Settings", level: "Beginner", time: "18 min" },
    ],
  };

  const data = categoryData[category] || categoryData.ai;
  const tutorials = allTutorials[category] || allTutorials.ai;

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    :root {
      --ink: #0f1117;
      --ink-soft: #4a4f5e;
      --bg: #f7f8fa;
      --surface: #ffffff;
      --border: #e4e6ed;
      --radius: 14px;
      --shadow: 0 2px 12px rgba(15,17,23,.07);
      --grad: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    }
    
    [data-theme="dark"] {
      --ink: #f7f8fa;
      --ink-soft: #cbd5e1;
      --bg: #0f1117;
      --surface: #1a1f3a;
      --border: #2d3748;
    }
    
    body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--ink); }
    
    nav {
      position: sticky; top: 0; z-index: 100;
      background: rgba(247,248,250,.88);
      backdrop-filter: blur(14px);
      border-bottom: 1px solid var(--border);
      padding: 0 2rem;
      display: flex; align-items: center; justify-content: space-between;
      height: 64px;
    }
    [data-theme="dark"] nav { background: rgba(15,17,23,.88); }
    
    .nav-back { cursor: pointer; font-size: 1.2rem; color: var(--ink); transition: opacity .2s; }
    .nav-back:hover { opacity: .6; }
    
    .nav-theme-toggle {
      background: transparent; border: none; cursor: pointer;
      font-size: 1.2rem; color: var(--ink);
      padding: .4rem; border-radius: 6px;
      transition: background .2s;
    }
    .nav-theme-toggle:hover { background: rgba(102,126,234,.1); }
    
    .header {
      padding: 4rem 2rem;
      background: linear-gradient(180deg, rgba(102,126,234,.08) 0%, rgba(118,75,162,.04) 100%);
      text-align: center;
    }
    
    .header-icon { font-size: 4rem; margin-bottom: 1rem; }
    
    h1 {
      font-family: 'DM Serif Display', serif;
      font-size: 2.4rem;
      margin-bottom: .5rem;
      background: var(--grad);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .header p { color: var(--ink-soft); font-size: 1.1rem; max-width: 600px; margin: 0 auto; }
    
    .tools-section {
      max-width: 1180px;
      margin: 4rem auto;
      padding: 0 2rem;
    }
    
    h2 {
      font-family: 'DM Serif Display', serif;
      font-size: 1.8rem;
      margin-bottom: 2rem;
      color: var(--ink);
    }
    
    .tools-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.5rem;
      margin-bottom: 4rem;
    }
    
    .tool-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 2rem;
      text-decoration: none;
      color: inherit;
      transition: all .2s;
      cursor: pointer;
    }
    
    .tool-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(102,126,234,.2);
      border-color: rgba(102,126,234,.5);
    }
    
    .tool-icon {
      width: 60px;
      height: 60px;
      border-radius: 12px;
      margin-bottom: 1rem;
      display: inline-block;
    }
    
    .tool-card h3 { font-size: 1.2rem; margin-bottom: .5rem; }
    .tool-card p { color: var(--ink-soft); font-size: .95rem; }
    
    .tutorials-section {
      max-width: 1180px;
      margin: 4rem auto;
      padding: 0 2rem;
    }
    
    .tutorial-list { display: flex; flex-direction: column; gap: 1rem; }
    
    .tut-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      text-decoration: none;
      color: inherit;
      transition: all .2s;
      cursor: pointer;
    }
    
    .tut-card:hover {
      box-shadow: 0 8px 24px rgba(102,126,234,.15);
      transform: translateX(4px);
    }
    
    .tut-info h3 { margin-bottom: .3rem; }
    .tut-info p { color: var(--ink-soft); font-size: .9rem; }
    
    .tut-meta {
      display: flex;
      gap: 1rem;
      align-items: center;
      white-space: nowrap;
    }
    
    .tut-level {
      font-size: .75rem;
      font-weight: 600;
      padding: .3rem .6rem;
      border-radius: 99px;
      background: rgba(102,126,234,.1);
      color: #667eea;
    }
    
    .tut-time { color: var(--ink-soft); font-size: .9rem; }
    
    footer {
      border-top: 1px solid var(--border);
      padding: 2rem;
      text-align: center;
      color: var(--ink-soft);
      margin-top: 4rem;
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <nav>
        <div className="nav-back" onClick={() => navigate('/')}>← Back</div>
        <button className="nav-theme-toggle" onClick={toggleTheme}>
          {isDarkMode ? '☀️' : '🌙'}
        </button>
      </nav>

      <div className="header">
        <div className="header-icon">{data.icon}</div>
        <h1>{data.name}</h1>
        <p>{data.desc}</p>
      </div>

      <div className="tools-section">
        <h2>Featured Tools</h2>
        <div className="tools-grid">
          {data.tools.map((tool, idx) => (
            <div key={idx} className="tool-card">
              <div className="tool-icon" style={{ backgroundColor: tool.color + '20' }}></div>
              <h3>{tool.name}</h3>
              <p>{tool.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="tutorials-section">
        <h2>Tutorials & Resources</h2>
        <div className="tutorial-list">
          {tutorials.map((tut, idx) => (
            <div 
              key={idx} 
              className="tut-card"
              onClick={() => navigate(`/tutorial/${idx}?cat=${category}`)}
            >
              <div className="tut-info">
                <h3>{tut.title}</h3>
                <p>Learn practical techniques and best practices</p>
              </div>
              <div className="tut-meta">
                <span className="tut-level">{tut.level}</span>
                <span className="tut-time">⏱ {tut.time}</span>
                <span>→</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <footer>
        <p>© 2026 flycodeinv.com · All Rights Reserved</p>
      </footer>
    </>
  );
}
